/**
 * API DIFFERENCES vs ../avatar.tsx (shadcn):
 * - Composition: Avatar + AvatarImage + AvatarFallback with default composition
 *   vs single Avatar (accepts src/alt/name props or children for override)
 * - Size: className-based (any) vs preset `size` with values 'sm'|'md'
 * - Fallback: separate component with auto-coloring vs inline initials
 * - Extras: AvatarGroup, getAccentColorName exports
 * - Initials: single letter for 1 word vs 2 letters
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import type { ComponentProps } from 'react'
import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'
import { hashString } from '../lib/presence-colour'
import { cn } from '../lib/utils'
import { Tooltip, TooltipPopup, TooltipTrigger } from './tooltip'

// oxlint-disable-next-line no-warning-comments -- TODO preserved per repo policy
// TODO: shapes like in MD3?

const avatarSizes = {
  xs: 'size-6',
  sm: 'size-8',
  default: 'size-10',
  lg: 'size-12',
  xl: 'size-14',
  '2xl': 'size-18 sm:size-25',
} as const

function Avatar({
  className,
  children,
  src,
  alt,
  name,
  size = 'sm',
  ...props
}: AvatarPrimitive.Root.Props & {
  src?: string
  alt?: string
  name?: string
  size?: keyof typeof avatarSizes
}) {
  if (children && (src || name)) {
    throw new Error(
      'Avatar: Cannot use both children and high-level props (src/name). Use either composition (children) or high-level API (src/name props).',
    )
  }

  return (
    <AvatarPrimitive.Root
      className={cn(
        'bg-surface-1 @container inline-flex items-center justify-center overflow-hidden rounded-full font-medium select-none',
        avatarSizes[size],
        className,
      )}
      data-slot="avatar"
      {...props}
    >
      {children ?? (
        <>
          {src && <AvatarImage src={src} alt={alt ?? name ?? ''} />}
          {name && <AvatarFallback>{name}</AvatarFallback>}
        </>
      )}
    </AvatarPrimitive.Root>
  )
}

function AvatarImage({ className, ...props }: AvatarPrimitive.Image.Props) {
  return (
    <AvatarPrimitive.Image
      className={cn('size-full object-cover', className)}
      data-slot="avatar-image"
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  children,
  ...props
}: AvatarPrimitive.Fallback.Props) {
  if (typeof children !== 'string' && typeof children !== 'number')
    throw new Error('AvatarFallback must have a string or number child.')

  const isNumber = typeof children === 'number'
  const content = isNumber ? `+${children}` : getInitials(children)
  const colorClass = isNumber
    ? 'bg-secondary/20 dark:bg-secondary/20 text-secondary'
    : getAccentColor(children)

  const textSizeClass = isNumber ? 'text-[30cqi]' : 'text-[40cqi]'

  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'flex size-full items-center justify-center rounded-full',
        colorClass,
        className,
      )}
      data-slot="avatar-fallback"
      {...props}
    >
      <span className={cn('element', textSizeClass)}>{content}</span>
    </AvatarPrimitive.Fallback>
  )
}

interface AvatarData {
  /** Stable identity (e.g. userId); falls back to `name`. Two people with one name need distinct ids. */
  id?: string
  src?: string
  alt?: string
  name: string
  ringColour?: AccentColorName
  /** Drawn faded, e.g. a collaborator whose tab is not active. */
  dimmed?: boolean
}

const ringColours: Record<AccentColorName, string> = {
  pink: 'ring-pink',
  red: 'ring-red',
  orange: 'ring-orange',
  yellow: 'ring-yellow',
  lightgreen: 'ring-lightgreen',
  green: 'ring-green',
  lightblue: 'ring-lightblue',
  blue: 'ring-blue',
  purple: 'ring-purple',
  violet: 'ring-violet',
  gray: 'ring-gray',
}

interface AvatarGroupProps extends Omit<ComponentProps<'div'>, 'children'> {
  avatars: AvatarData[]
  maxVisible?: number
  size?: string
}

/**
 * A row of avatars: each one a focusable button named by the person, with their name as a tooltip, then one "+N"
 * avatar for the rest. Data hooks: `data-slot="avatar-group-item"` with `data-id` and `data-dimmed`, and
 * `data-more` on the "+N" avatar. Pass `aria-label` to name the group.
 */
function AvatarGroup({
  avatars,
  maxVisible = 3,
  className,
  size,
  ...props
}: AvatarGroupProps) {
  const visibleAvatars = avatars.slice(0, maxVisible)
  const remainingCount = avatars.length - maxVisible

  return (
    <div
      role="group"
      data-slot="avatar-group"
      className={cn('flex items-center', className)}
      {...props}
    >
      {visibleAvatars.map((avatar, i) => (
        <Tooltip key={avatar.id ?? avatar.name}>
          <TooltipTrigger
            render={(triggerProps) => (
              <button
                {...triggerProps}
                type="button"
                data-slot="avatar-group-item"
                data-id={avatar.id ?? avatar.name}
                data-dimmed={avatar.dimmed === true}
                aria-label={avatar.name}
                className={cn(
                  'rounded-full',
                  i > 0 && '-ml-[8%]',
                  avatar.dimmed && 'opacity-50',
                )}
              >
                <Avatar
                  className={cn(
                    'ring-2',
                    avatar.ringColour
                      ? ringColours[avatar.ringColour]
                      : 'ring-surface-1',
                    size,
                  )}
                >
                  {avatar.src && (
                    <AvatarImage
                      src={avatar.src}
                      alt={avatar.alt ?? avatar.name}
                    />
                  )}
                  <AvatarFallback>{avatar.name}</AvatarFallback>
                </Avatar>
              </button>
            )}
          />
          <TooltipPopup>{avatar.name}</TooltipPopup>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Avatar
          role="img"
          aria-label={`${remainingCount} more`}
          data-more
          className={cn('ring-surface-1 -ml-[8%] ring-2', size)}
        >
          <AvatarFallback>{remainingCount}</AvatarFallback>
        </Avatar>
      )}
    </div>
  )
}

/** Extracts initials from a name.
 *
 * @cases
 * * "Alice" → "A"
 * * "John Doe" → "JD"
 * * "Jean Pierre Dupont" → "JD"
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/u)

  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  const lastPart = parts.at(-1) ?? ''
  return (parts[0].charAt(0) + lastPart.charAt(0)).toUpperCase()
}

const accentColorNames = [
  'pink',
  'red',
  'orange',
  'yellow',
  'lightgreen',
  'green',
  'lightblue',
  'blue',
  'purple',
  'violet',
  'gray',
] as const

type AccentColorName = (typeof accentColorNames)[number]
function getAccentColorName(name: string): AccentColorName {
  return accentColorNames[hashString(name) % accentColorNames.length]
}

const accentColors = [
  'bg-pink/20 dark:bg-pink/20 text-pink',
  'bg-red/20 dark:bg-red/20 text-red',
  'bg-orange/20 dark:bg-orange/20 text-orange',
  'bg-yellow/20 dark:bg-yellow/20 text-yellow',
  'bg-lightgreen/20 dark:bg-lightgreen/20 text-lightgreen',
  'bg-green/20 dark:bg-green/20 text-green',
  'bg-lightblue/20 dark:bg-lightblue/20 text-lightblue',
  'bg-blue/20 dark:bg-blue/20 text-blue',
  'bg-purple/20 dark:bg-purple/20 text-purple',
  'bg-violet/20 dark:bg-violet/20 text-violet',
  'bg-gray/20 dark:bg-gray/20 text-gray',
] as const

function getAccentColor(name: string): string {
  return accentColors[hashString(name) % accentColors.length]
}

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  getAccentColorName,
  type AccentColorName,
  type AvatarData,
}
